import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Link from "next/link";
import { FC } from "react";
import { Container } from "~/components/Container";
import Hero from "~/components/ResourceSections/Hero";
import Text from "~/components/Text";
import Colors from "~/constants/colors";
import { privacyContent } from "~/constants/data";
import { convertToSlug } from "~/helpers/data.helpers";
import Trans from "~/types/translationKeys";

import styles from "~/styles/pages/PrivacyPolicy.module.scss";

export const getServerSideProps: GetServerSideProps = async (context) => {
  return {
    props: {
      ...(await serverSideTranslations(context.locale || "en", ["common"])),
    },
  };
};

const PrivacyPolicy: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-white">
      <Hero title={t(Trans.privacy_policy)} />
      <Container className={styles.privacy_policy}>
        <div className="flex flex-col h-max gap-6 md:flex-row md:gap-8 lg:gap-[125px]">
          <div className="flex flex-col gap-6">
            <div className="hidden flex-col gap-6 order-[-2] md:flex">
              <Text color={Colors.GREY_400}>
                {t(Trans.last_updated)}: {t(Trans.last_updated_date)}
              </Text>
              <p className="text-sm">
                {t(
                  Trans.welcome_to_cx_genie_the_powerful_and_optimized_saas_platform_designed_to_enhance_customer_engagement_with_minimal_effort_at_cx_genie_we_are_committed_to_protecting_your_privacy_and_the_security_of_your_personal_information_this_privacy_policy_explains_how_we_collect_use_disclose_and_safeguard_your_data_by_using_cx_genie_you_consent_to_the_practices_described_in_this_privacy_policy
                )}
              </p>
            </div>

            {privacyContent.map((item, itemIndex) => (
              <div
                className="flex flex-col gap-3"
                id={convertToSlug(item.title)}
                key={convertToSlug(item.title)}
              >
                <Text type="headline-5" color={Colors.GREY_700}>
                  {itemIndex + 1}. {t(item.title)}
                </Text>
                {item.list.map((child, childIndex) => (
                  <div className="flex flex-col gap-2" key={childIndex}>
                    <Text type="title-3" color={Colors.GREY_700}>
                      {itemIndex + 1}.{childIndex + 1} {t(child.title)}
                    </Text>
                    <Text color={Colors.GREY_600}>{t(child.content)}</Text>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-6 order-[-2] md:hidden">
            <Text color={Colors.GREY_400}>
              {t(Trans.last_updated)}: {t(Trans.last_updated_date)}
            </Text>
            <p className="text-sm">
              {t(
                Trans.welcome_to_cx_genie_the_powerful_and_optimized_saas_platform_designed_to_enhance_customer_engagement_with_minimal_effort_at_cx_genie_we_are_committed_to_protecting_your_privacy_and_the_security_of_your_personal_information_this_privacy_policy_explains_how_we_collect_use_disclose_and_safeguard_your_data_by_using_cx_genie_you_consent_to_the_practices_described_in_this_privacy_policy
              )}
            </p>
          </div>
          <div className={styles.table_content}>
            <Text type="title-1">{t(Trans.table_of_content)}</Text>
            <hr />
            <div className="flex flex-col gap-3">
              {privacyContent.map((item, index) => (
                <Link
                  href={`#${convertToSlug(item.title)}`}
                  key={index}
                  className="flex items-start gap-1"
                >
                  <Text
                    type="title-3"
                    color={Colors.GREY_500}
                    className="min-w-[22px]"
                  >
                    {index + 1}.
                  </Text>
                  <Text type="title-3" color={Colors.GREY_500}>
                    {t(item.title)}
                  </Text>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
